import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Store, Utensils, ShoppingBag, HeartPulse, Plane, GraduationCap, Ticket,
  Wrench, Tag, Plus, Pencil, Trash2, Loader2, Copy, ExternalLink, Phone,
  CalendarClock, Upload, Handshake, Star, Eye, ArrowDownWideNarrow, Check, X,
  Home, Car, Coffee, Gift, Dumbbell, Scissors, Smartphone, Fuel,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api, fileUrl } from "../lib/api";

export const CATEGORIES = [
  { key: "مطاعم", Icon: Utensils, cls: "bg-orange-100 text-orange-700" },
  { key: "متاجر", Icon: ShoppingBag, cls: "bg-blue-100 text-blue-700" },
  { key: "صحة", Icon: HeartPulse, cls: "bg-rose-100 text-rose-700" },
  { key: "سفر", Icon: Plane, cls: "bg-sky-100 text-sky-700" },
  { key: "ترفيه", Icon: Ticket, cls: "bg-fuchsia-100 text-fuchsia-700" },
  { key: "تعليم", Icon: GraduationCap, cls: "bg-emerald-100 text-emerald-700" },
  { key: "خدمات", Icon: Wrench, cls: "bg-amber-100 text-amber-700" },
  { key: "عام", Icon: Tag, cls: "bg-slate-100 text-slate-600" },
];
const catOf = (k) => CATEGORIES.find((c) => c.key === k) || CATEGORIES[CATEGORIES.length - 1]; // eslint-disable-line no-unused-vars

export const ICON_OPTIONS = {
  store: Store, food: Utensils, shopping: ShoppingBag, health: HeartPulse,
  travel: Plane, fun: Ticket, edu: GraduationCap, services: Wrench,
  home: Home, car: Car, coffee: Coffee, gift: Gift,
  fitness: Dumbbell, beauty: Scissors, tech: Smartphone, fuel: Fuel, tag: Tag,
};
export const COLOR_OPTIONS = [
  { key: "orange", badge: "bg-orange-100 text-orange-700", dot: "bg-orange-500" },
  { key: "blue", badge: "bg-blue-100 text-blue-700", dot: "bg-blue-500" },
  { key: "rose", badge: "bg-rose-100 text-rose-700", dot: "bg-rose-500" },
  { key: "sky", badge: "bg-sky-100 text-sky-700", dot: "bg-sky-500" },
  { key: "fuchsia", badge: "bg-fuchsia-100 text-fuchsia-700", dot: "bg-fuchsia-500" },
  { key: "emerald", badge: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  { key: "amber", badge: "bg-amber-100 text-amber-700", dot: "bg-amber-500" },
  { key: "indigo", badge: "bg-indigo-100 text-indigo-700", dot: "bg-indigo-500" },
  { key: "slate", badge: "bg-slate-100 text-slate-600", dot: "bg-slate-400" },
];
// Resolve icon + color for any offer: fixed categories use CATEGORIES; custom use stored icon/color.
export const resolveCat = (offer) => {
  const fixed = CATEGORIES.find((c) => c.key === offer.category);
  if (fixed) return { Icon: fixed.Icon, cls: fixed.cls };
  const Icon = ICON_OPTIONS[offer.category_icon] || Tag;
  const color = COLOR_OPTIONS.find((c) => c.key === offer.category_color);
  return { Icon, cls: color?.badge || "bg-slate-100 text-slate-600" };
};
const AUD = { companies: "للشركات", employees: "للموظفين", both: "للجميع" };

const discountLabel = (o) => {
  if (o.discount_type === "percent" && o.discount_value) return `${o.discount_value}%`;
  if (o.discount_type === "amount" && o.discount_value) return `${o.discount_value} ⃁`;
  return o.discount_text || "خصم خاص";
};

export const OfferCard = ({ offer, showAudience, onEdit, onDelete }) => {
  const c = resolveCat(offer);
  const copy = () => { navigator.clipboard?.writeText(offer.promo_code); api.trackOffer(offer.id, "copy"); toast.success("تم نسخ الكود"); };
  return (
    <div data-testid={`offer-card-${offer.id}`} className={`relative flex flex-col rounded-2xl border bg-white overflow-hidden hover:shadow-md transition ${offer.featured ? "border-amber-300 ring-2 ring-amber-300/60" : "border-slate-200"}`}>
      {offer.featured && (
        <span data-testid={`offer-featured-${offer.id}`} className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 text-white text-[10px] font-bold px-2 py-0.5 shadow"><Star className="h-3 w-3 fill-white" /> مميّز</span>
      )}
      <div className="relative h-28 bg-gradient-to-br from-slate-800 to-slate-600 flex items-center justify-center">
        {offer.logo_id ? (
          <img src={fileUrl(offer.logo_id)} alt={offer.partner_name} className="h-full w-full object-cover" />
        ) : (
          <c.Icon className="h-12 w-12 text-white/70" />
        )}
        <span className={`absolute ${offer.featured ? "top-10" : "top-2"} right-2 rounded-full bg-white/95 text-amber-700 font-bold text-sm px-3 py-1 shadow-sm font-mono-nums`}>{discountLabel(offer)}</span>
        {showAudience && <span className="absolute top-2 left-2 rounded-full bg-black/50 text-white text-[10px] font-semibold px-2 py-0.5">{AUD[offer.audience] || "للجميع"}{offer.active === false ? " · موقوف" : ""}</span>}
      </div>
      <div className="flex-1 p-4">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 rounded-full text-[10px] font-semibold px-2 py-0.5 ${c.cls}`}><c.Icon className="h-3 w-3" />{offer.category || "عام"}</span>
        </div>
        <p className="mt-1.5 font-heading font-bold text-slate-800">{offer.partner_name}</p>
        {offer.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{offer.description}</p>}
        {offer.promo_code && (
          <button onClick={copy} data-testid={`offer-copy-${offer.id}`} className="mt-3 flex w-full items-center justify-between rounded-lg border border-dashed border-amber-300 bg-amber-50 px-3 py-1.5 text-sm hover:bg-amber-100 transition">
            <span className="font-mono-nums font-bold text-amber-800 tracking-wider">{offer.promo_code}</span>
            <Copy className="h-3.5 w-3.5 text-amber-600" />
          </button>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-400">
          {offer.expires_at && <span className="flex items-center gap-1"><CalendarClock className="h-3 w-3" /> حتى {offer.expires_at}</span>}
          {offer.phone && <a href={`tel:${offer.phone}`} className="flex items-center gap-1 text-slate-500 hover:text-slate-700"><Phone className="h-3 w-3" /> {offer.phone}</a>}
        </div>
        {offer.url && (
          <a href={offer.url.startsWith("http") ? offer.url : `https://${offer.url}`} target="_blank" rel="noreferrer" data-testid={`offer-link-${offer.id}`}
            onClick={() => api.trackOffer(offer.id, "open")}
            className="mt-3 flex items-center justify-center gap-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-sm font-semibold py-2 transition">
            زيارة الشريك <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
        {showAudience && (
          <div className="mt-3 flex items-center gap-4 border-t border-slate-100 pt-2 text-[11px] text-slate-400" data-testid={`offer-stats-${offer.id}`}>
            <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {Number(offer.views || 0).toLocaleString("en-US")} فتح</span>
            <span className="flex items-center gap-1"><Copy className="h-3 w-3" /> {Number(offer.copies || 0).toLocaleString("en-US")} نسخ كود</span>
          </div>
        )}
      </div>
      {(onEdit || onDelete) && (
        <div className="flex border-t border-slate-100">
          {onEdit && <button onClick={() => onEdit(offer)} data-testid={`offer-edit-${offer.id}`} className="flex-1 flex items-center justify-center gap-1 py-2 text-sm text-slate-600 hover:bg-slate-50"><Pencil className="h-4 w-4" /> تعديل</button>}
          {onDelete && <button onClick={() => onDelete(offer)} data-testid={`offer-delete-${offer.id}`} className="flex-1 flex items-center justify-center gap-1 py-2 text-sm text-rose-600 hover:bg-rose-50 border-r border-slate-100"><Trash2 className="h-4 w-4" /> حذف</button>}
        </div>
      )}
    </div>
  );
};

const empty = { partner_name: "", category: "مطاعم", category_icon: "", category_color: "", discount_type: "percent", discount_value: "", discount_text: "", promo_code: "", description: "", url: "", phone: "", expires_at: "", logo_id: "", audience: "both", active: true, featured: false };

const OfferForm = ({ offer, extraCategories = [], categoryMeta = {}, onClose, onSaved }) => {
  const [f, setF] = useState({ ...empty, ...(offer || {}) });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [addingCat, setAddingCat] = useState(false);
  const [newCat, setNewCat] = useState("");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  // Radix Select locks body pointer-events while open; when we swap it out (add-new/confirm) the reset can leak. Force-unlock.
  const unlockBody = () => setTimeout(() => { try { document.body.style.pointerEvents = ""; } catch (e) { /* noop */ } }, 0);
  const catKeys = [...new Set([...CATEGORIES.map((c) => c.key), ...extraCategories, ...(f.category ? [f.category] : [])].filter(Boolean))];
  const isCustomCat = !!f.category && !CATEGORIES.some((c) => c.key === f.category);
  const confirmNewCat = () => {
    const v = newCat.trim();
    if (!v) { toast.error("اكتب اسم الفئة"); return; }
    setF((p) => ({ ...p, category: v, category_icon: p.category_icon || "gift", category_color: p.category_color || "indigo" }));
    setAddingCat(false); setNewCat(""); unlockBody();
  };
  const pickCategory = (v) => {
    if (v === "__add__") { setAddingCat(true); setNewCat(""); unlockBody(); return; }
    const m = categoryMeta[v];
    setF((p) => ({ ...p, category: v, category_icon: m?.icon || "", category_color: m?.color || "" }));
    unlockBody();
  };

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { const r = await api.uploadPartnerLogo(file); set("logo_id", r.logo_id); toast.success("تم رفع الشعار"); }
    catch (err) { toast.error(err?.response?.data?.detail || "تعذّر رفع الصورة"); }
    finally { setUploading(false); }
  };
  const save = async () => {
    if (!f.partner_name.trim()) { toast.error("اسم الشريك مطلوب"); return; }
    setBusy(true);
    const body = { ...f, discount_value: parseFloat(f.discount_value) || 0 };
    try {
      if (offer?.id) await api.updateOffer(offer.id, body); else await api.createOffer(body);
      toast.success("تم حفظ العرض"); onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "تعذّر الحفظ"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg font-body max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="offer-form-dialog">
        <DialogHeader className="text-start"><DialogTitle className="font-heading text-lg flex items-center gap-2"><Handshake className="h-5 w-5 text-indigo-600" /> {offer?.id ? "تعديل عرض" : "إضافة عرض شريك"}</DialogTitle>
          <DialogDescription className="sr-only">نموذج عرض شريك</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="h-16 w-16 shrink-0 rounded-xl border border-slate-200 bg-slate-50 overflow-hidden grid place-items-center">
              {f.logo_id ? <img src={fileUrl(f.logo_id)} alt="logo" className="h-full w-full object-cover" /> : <Store className="h-6 w-6 text-slate-300" />}
            </div>
            <label className="flex-1">
              <span className="text-[11px] text-slate-500">شعار الشريك</span>
              <div className="mt-1 flex items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2 cursor-pointer hover:bg-slate-50">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : <Upload className="h-4 w-4 text-slate-400" />}
                <span className="text-sm text-slate-500">{uploading ? "جارٍ الرفع..." : "رفع صورة (PNG/JPG)"}</span>
                <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="offer-logo-input" />
              </div>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="text-[11px] text-slate-500">اسم الشريك *</span>
              <Input value={f.partner_name} onChange={(e) => set("partner_name", e.target.value)} className="h-9 text-sm" data-testid="offer-name" /></label>
            <label className="block"><span className="text-[11px] text-slate-500">الفئة</span>
              {addingCat ? (
                <div className="mt-1 flex items-center gap-1">
                  <Input autoFocus value={newCat} onChange={(e) => setNewCat(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirmNewCat(); } }}
                    placeholder="اسم الفئة الجديدة" className="h-9 text-sm" data-testid="offer-category-new-input" />
                  <Button type="button" size="sm" className="h-9 px-2 bg-indigo-600 hover:bg-indigo-700" onClick={confirmNewCat} data-testid="offer-category-new-confirm"><Check className="h-4 w-4" /></Button>
                  <Button type="button" size="sm" variant="outline" className="h-9 px-2" onClick={() => { setAddingCat(false); setNewCat(""); }} data-testid="offer-category-new-cancel"><X className="h-4 w-4" /></Button>
                </div>
              ) : (
                <Select value={f.category} onValueChange={pickCategory}>
                  <SelectTrigger className="h-9 text-sm" data-testid="offer-category"><SelectValue /></SelectTrigger>
                  <SelectContent position="item-aligned">
                    {catKeys.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                    <SelectItem value="__add__" data-testid="offer-category-add-item" className="text-indigo-600 font-semibold">
                      <span className="flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> إضافة فئة جديدة…</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            </label>
          </div>
          {isCustomCat && !addingCat && (
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-2.5" data-testid="offer-cat-style">
              <p className="text-[11px] font-semibold text-slate-500">أيقونة ولون الفئة «{f.category}»</p>
              <div className="flex flex-wrap gap-1.5" data-testid="offer-cat-icons">
                {Object.entries(ICON_OPTIONS).map(([key, Ic]) => (
                  <button type="button" key={key} data-testid={`offer-cat-icon-${key}`} onClick={() => set("category_icon", key)}
                    className={`h-9 w-9 grid place-items-center rounded-lg border transition ${f.category_icon === key ? "border-indigo-500 bg-indigo-50 text-indigo-600 ring-2 ring-indigo-200" : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"}`}>
                    <Ic className="h-4 w-4" />
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="offer-cat-colors">
                {COLOR_OPTIONS.map((c) => (
                  <button type="button" key={c.key} data-testid={`offer-cat-color-${c.key}`} onClick={() => set("category_color", c.key)}
                    className={`h-7 w-7 rounded-full ${c.dot} transition ${f.category_color === c.key ? "ring-2 ring-offset-2 ring-slate-700 scale-110" : "opacity-80 hover:opacity-100"}`} aria-label={c.key} />
                ))}
              </div>
              <div className="pt-1">
                <span className={`inline-flex items-center gap-1 rounded-full text-[11px] font-semibold px-2.5 py-1 ${resolveCat(f).cls}`} data-testid="offer-cat-preview">
                  {(() => { const P = resolveCat(f).Icon; return <P className="h-3.5 w-3.5" />; })()} {f.category}
                </span>
              </div>
            </div>
          )}
          <div className="grid grid-cols-3 gap-2">
            <label className="block"><span className="text-[11px] text-slate-500">نوع الخصم</span>
              <Select value={f.discount_type} onValueChange={(v) => set("discount_type", v)}>
                <SelectTrigger className="h-9 text-sm" data-testid="offer-discount-type"><SelectValue /></SelectTrigger>
                <SelectContent position="item-aligned">
                  <SelectItem value="percent">نسبة %</SelectItem>
                  <SelectItem value="amount">مبلغ ⃁</SelectItem>
                  <SelectItem value="text">نص حر</SelectItem>
                </SelectContent>
              </Select></label>
            {f.discount_type === "text" ? (
              <label className="block col-span-2"><span className="text-[11px] text-slate-500">نص الخصم</span>
                <Input value={f.discount_text} onChange={(e) => set("discount_text", e.target.value)} className="h-9 text-sm" placeholder="مثال: هدية مع كل طلب" data-testid="offer-discount-text" /></label>
            ) : (
              <label className="block col-span-2"><span className="text-[11px] text-slate-500">قيمة الخصم</span>
                <Input type="number" dir="ltr" value={f.discount_value} onChange={(e) => set("discount_value", e.target.value)} className="h-9 text-sm font-mono-nums" data-testid="offer-discount-value" /></label>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="text-[11px] text-slate-500">كود الخصم</span>
              <Input value={f.promo_code} onChange={(e) => set("promo_code", e.target.value)} className="h-9 text-sm" data-testid="offer-code" /></label>
            <label className="block"><span className="text-[11px] text-slate-500">تاريخ الانتهاء</span>
              <Input type="date" value={f.expires_at} onChange={(e) => set("expires_at", e.target.value)} className="h-9 text-sm" data-testid="offer-expires" /></label>
          </div>
          <label className="block"><span className="text-[11px] text-slate-500">وصف مختصر</span>
            <Textarea rows={2} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="offer-desc" /></label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="text-[11px] text-slate-500">رابط</span>
              <Input dir="ltr" value={f.url} onChange={(e) => set("url", e.target.value)} className="h-9 text-sm" placeholder="https://" data-testid="offer-url" /></label>
            <label className="block"><span className="text-[11px] text-slate-500">جوال</span>
              <Input dir="ltr" value={f.phone} onChange={(e) => set("phone", e.target.value)} className="h-9 text-sm" data-testid="offer-phone" /></label>
          </div>
          <div className="grid grid-cols-2 gap-2 items-end">
            <label className="block"><span className="text-[11px] text-slate-500">تظهر لـ</span>
              <Select value={f.audience} onValueChange={(v) => set("audience", v)}>
                <SelectTrigger className="h-9 text-sm" data-testid="offer-audience"><SelectValue /></SelectTrigger>
                <SelectContent position="item-aligned">
                  <SelectItem value="both">للجميع (شركات وموظفين)</SelectItem>
                  <SelectItem value="companies">الشركات فقط</SelectItem>
                  <SelectItem value="employees">الموظفين فقط</SelectItem>
                </SelectContent>
              </Select></label>
            <label className="flex items-center gap-2 pb-2 cursor-pointer"><input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} data-testid="offer-active" className="h-4 w-4 accent-indigo-600" /><span className="text-sm text-slate-600">مُفعّل</span></label>
          </div>
          <label className="flex items-center gap-2 cursor-pointer rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2">
            <input type="checkbox" checked={f.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="offer-featured-toggle" className="h-4 w-4 accent-amber-500" />
            <Star className="h-4 w-4 text-amber-500" />
            <span className="text-sm text-slate-700 font-semibold">عرض مميّز</span>
            <span className="text-[11px] text-slate-400">(يُثبّت في الأعلى بشارة ذهبية)</span>
          </label>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
          <Button onClick={save} disabled={busy || uploading} data-testid="offer-save" className="bg-indigo-600 hover:bg-indigo-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "حفظ العرض"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const PartnerOffersPanel = ({ isOwner }) => {
  const [offers, setOffers] = useState(null);
  const [editing, setEditing] = useState(null);
  const [sortMode, setSortMode] = useState("recent");

  const load = useCallback(async () => {
    try {
      const d = isOwner ? await api.adminOffers() : await api.partnerOffers();
      setOffers(d.offers || []);
    } catch { toast.error("تعذّر تحميل العروض"); setOffers([]); }
  }, [isOwner]);
  useEffect(() => { load(); }, [load]);

  const del = async (o) => { if (!window.confirm(`حذف عرض «${o.partner_name}»؟`)) return; try { await api.deleteOffer(o.id); load(); toast.success("تم الحذف"); } catch { toast.error("تعذّر الحذف"); } };

  const extraCategories = [...new Set((offers || []).map((o) => o.category).filter((c) => c && !CATEGORIES.some((x) => x.key === c)))];
  const categoryMeta = (offers || []).reduce((acc, o) => {
    if (o.category && !CATEGORIES.some((x) => x.key === o.category) && !acc[o.category] && (o.category_icon || o.category_color)) {
      acc[o.category] = { icon: o.category_icon || "", color: o.category_color || "" };
    }
    return acc;
  }, {});

  if (!offers) return <div className="py-16 text-center text-slate-300"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;

  const displayed = (isOwner && sortMode === "engagement")
    ? [...offers].sort((a, b) => ((b.views || 0) + (b.copies || 0)) - ((a.views || 0) + (a.copies || 0)))
    : offers;

  return (
    <div className="space-y-4" data-testid="partner-offers-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-heading font-bold text-slate-800 flex items-center gap-1.5"><Handshake className="h-5 w-5 text-indigo-600" /> عروض شركاء النجاح</p>
          <p className="text-[11px] text-slate-400">خصومات حصرية من شركاء المنصة{isOwner ? " — أنت تديرها لكل الشركات." : "."}</p>
        </div>
        <div className="flex items-center gap-2">
          {isOwner && offers.length > 0 && (
            <div className="flex items-center rounded-lg bg-slate-100 p-1" data-testid="offer-sort">
              <button onClick={() => setSortMode("recent")} data-testid="offer-sort-recent" className={`px-2.5 py-1 text-xs rounded-md transition ${sortMode === "recent" ? "bg-white shadow-sm font-semibold text-slate-800" : "text-slate-500"}`}>الأحدث</button>
              <button onClick={() => setSortMode("engagement")} data-testid="offer-sort-engagement" className={`px-2.5 py-1 text-xs rounded-md transition flex items-center gap-1 ${sortMode === "engagement" ? "bg-white shadow-sm font-semibold text-slate-800" : "text-slate-500"}`}><ArrowDownWideNarrow className="h-3.5 w-3.5" /> الأكثر تفاعلًا</button>
            </div>
          )}
          {isOwner && <Button onClick={() => setEditing({})} data-testid="offer-add-btn" className="bg-indigo-600 hover:bg-indigo-700 gap-1.5"><Plus className="h-4 w-4" /> إضافة عرض</Button>}
        </div>
      </div>

      {offers.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 py-14 text-center" data-testid="offers-empty">
          <Handshake className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-2 text-slate-500 text-sm">{isOwner ? "لا توجد عروض بعد. أضف أول عرض شريك." : "لا توجد عروض متاحة حاليًا."}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {displayed.map((o) => (
            <OfferCard key={o.id} offer={o} showAudience={isOwner}
              onEdit={isOwner ? setEditing : undefined} onDelete={isOwner ? del : undefined} />
          ))}
        </div>
      )}

      {editing && <OfferForm offer={editing.id ? editing : null} extraCategories={extraCategories} categoryMeta={categoryMeta} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
};
